'use strict';

const _ = require('underscore');
const d3 = require('d3');
const Victor = require('victor');
const SVGUtil = require('common/ui/view/svg/util');
const SVGView = require('common/ui/view/svg/index');
const Dropshadow = require('common/ui/template/svg/dropshadow.hbs');

/**
 * Frame which can be zoomed and dragged.
 */
const FrameView = SVGView.extend({
    tagName: 'g',

    /**
     * @constructs
     * @extends SVGView
     *
     * @param {Number} [opts.init.x=0] - Initial x position
     * @param {Number} [opts.init.y=0] - Initial y position
     * @param {Number} [opts.init.scale=1.0] - Initial scale
     * @param {Number} opts.frame.width
     * @param {Number} opts.frame.height
     * @param {Number} opts.scale.min
     * @param {Number} opts.scale.max
     */
    initialize: function(opts) {
        FrameView.__super__.initialize.call(this, opts);
        _.bindAll(this, '_zoom', '_onclick', '_ondblclick',
            'zoomTo', 'resetZoom');
        this._zoomed = false;

        // override default initial parameters
        this._init = _.extend({
            x: 0, y: 0, scale: 0.3
        }, opts.init);
        this._scale = opts.scale;
        this._size = opts.size;
    },

    render: function() {
        if(this.getOption('includeDefs')) {
            this.$el.append('defs').html(Dropshadow());
        }

        this.zoomer = d3.zoom()
            .on('zoom', this._zoom)
            .on('end', this._onclick);

        // zoom の制限
        this.zoomer.scaleExtent([this._scale.min, this._scale.max]);
        this._base = this.$el;

        this._frame = this.$el
            .call(this.zoomer)
            .on('dblclick.zoom', null)
            .append('g')
            .on('mousedown', this.resetZoom);

        this._bg = this._frame
            .append('rect')
            .attr('class', 'frame-bg')
            .attr('width', this._size.width)
            .attr('height', this._size.height)
            .attr('transform', SVGUtil.transform({
                x: - this._size.width / 2,
                y: - this._size.height / 2
            }))
            .on('mouseup', this._onclick)
            .on('dblclick', this._ondblclick);

        this.$el = this._frame;

        return this;
    },

    enableZoom: function() {
        this._base.on('mousedown', null);
        this._base.on('mousemove', null);
        this._base.on('mouseup', null);
        this._base.call(this.zoomer);
        this._base.on('dblclick.zoom', null);
    },

    disableZoom: function() {
        this._base.on('.zoom', null);
        const self = this;
        this._base.on('mousedown', function() {
            const translate = d3.mouse(self.$el.node());
            const pos = {
                x: translate[0],
                y: translate[1]
            };
            self.trigger('dragstart', pos);
        });
        this._base.on('mousemove', function() {
            const translate = d3.mouse(self.$el.node());
            const pos = {
                x: translate[0],
                y: translate[1]
            };
            self.trigger('drag', pos);
        });
        this._base.on('mouseup', function() {
            self.trigger('dragend');
        });
    },

    resetZoom: function() {
        this._zoomed = false;
    },

    _zoom: function() {
        this._zoomed = true;
        let transform = d3.event.transform;
        this._frame.attr('transform', transform);
        this.trigger('zoom', {
            x: transform.x,
            y: transform.y,
        }, transform.k);
    },

    _onclick: function() {
        if(this._zoomed) {
            return;
        }

        let pos = d3.mouse(this.$el.node());
        if(!pos) {
            pos = d3.touches(this.$el.node())[0];
        }
        this.trigger('click', {
            x: pos[0],
            y: pos[1]
        }, this.scale());
    },

    _ondblclick: function() {
        if(this._zoomed) {
            return;
        }

        let pos = d3.mouse(this.$el.node());
        if(!pos) {
            pos = d3.touches(this.$el.node())[0];
        }
        this.trigger('dblclick', {
            x: pos[0],
            y: pos[1]
        }, this.scale());
    },

    width: function() {
        return this._size.width;
    },

    height: function() {
        return this._size.height;
    },

    /**
     * Zooming or panning to the specified point with animation.
     *
     * @param {Number} params.x - x position zooming to
     * @param {Number} params.y - y position zooming to
     * @param {Number} [params.scale=current scale]
     * @param {Number} [params.duration=1000(ms)] - animation duration.
     */
    zoomTo: function(params) {
        const scale = params.scale ? params.scale : d3.zoomTransform(this._base.node()).k;
        const duration = _.has(params, 'duration') && !_.isUndefined(params.duration) ? params.duration : 1000;
        const x = - params.x * scale;
        const y = - params.y * scale;

        const transform = d3.zoomIdentity;

        if(duration == 0) {
            this._base
                .call(this.zoomer.transform,
                    transform.translate(x, y).scale(scale));
        }
        else {
            this._base
                .transition()
                .duration(duration)
                .call(this.zoomer.transform,
                    transform.translate(x, y).scale(scale));
        }
    },

    /**
     * Set transform to specified parameter
     *
     * @param {Number} params.x
     * @param {Number} params.y
     * @param {Number} params.scal
     */
    transform: function(params) {
        const transform = d3.zoomIdentity;

        this._base
            .call(this.zoomer.transform,
                transform.translate(params.x, params.y).scale(params.scale));
    },

    /**
     * Select elements.
     *
     * Wrapper of the d3.selectAll method.
     *
     * @param {string} selector - css selector: e.g.) '.node'
     * @return {d3.selection} - d3 selection object.
     */
    selectAll: function(selector) {
        return this.$el.selectAll(selector);
    },

    /**
     * Return current scale.
     *
     * @return {float} - current scale.
     */
    scale: function() {
        return SVGUtil.getTransform(this.$el).k;
    },

    /**
     * Return current center position.
     *
     * @return {Object} object - current center position.
     */
    center: function() {
        const t = SVGUtil.getTransform(this.$el);
        return {
            x: -t.x / t.k,
            y: -t.y / t.k
        };
    },

    centerVector: function() {
        const t = SVGUtil.getTransform(this.$el);
        return Victor.fromObject({
            x: -t.x / t.k,
            y: -t.y / t.k
        });
    }
});

module.exports = FrameView;



// WEBPACK FOOTER //
// ./common/ui/view/svg/frame.js